// src/hooks/useUserDiscountPreference.js
//
// Preferencia "comprar CON o SIN descuento" del cliente (task-1488).
//
// El toggle lo muestra el resumen de compra, pero también lo necesitan el checkout
// (para decidir si oculta la factura y si el envío es solo retiro) y el cálculo de
// envío. Para compartirlo entre componentes que no están anidados se guarda en
// localStorage y se avisa con un evento, el mismo patrón que ya usa el checkout con
// 'shipping-method-updated'.

import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'apply_user_discount';
const EVENT_NAME = 'user-discount-preference-updated';

const isBrowser = () => typeof window !== 'undefined';

/** Lee la preferencia. Por defecto true: si no eligió nada, se compra con descuento. */
export const readApplyUserDiscount = () => {
  if (!isBrowser()) return true;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return true;
    return raw === '1' || raw === 'true';
  } catch (e) {
    return true;
  }
};

/** Escribe la preferencia y notifica a los demás componentes montados. */
export const writeApplyUserDiscount = (value) => {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(STORAGE_KEY, value ? '1' : '0');
  } catch (e) {
    // Modo privado / storage lleno: se sigue emitiendo el evento para que la
    // pantalla reaccione aunque no se pueda persistir.
  }
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { value: !!value } }));
};

/**
 * @returns {[boolean, (value:boolean)=>void]} preferencia actual y setter.
 */
export default function useUserDiscountPreference() {
  const [applyDiscount, setApplyDiscountState] = useState(readApplyUserDiscount);

  useEffect(() => {
    const sync = () => setApplyDiscountState(readApplyUserDiscount());
    window.addEventListener(EVENT_NAME, sync);
    window.addEventListener('storage', sync); // por si cambia en otra pestaña
    return () => {
      window.removeEventListener(EVENT_NAME, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const setApplyDiscount = useCallback((value) => {
    setApplyDiscountState(!!value);
    writeApplyUserDiscount(!!value);
  }, []);

  return [applyDiscount, setApplyDiscount];
}
